import { COUNTRY_ALPHA2_CODES } from "@/lib/countries";

/**
 * IR-661 Phase 7 Task 12 — PhoneAppdata sub-form.
 *
 * Backend reality (Task 5, api/ops/serializers_data.py:212-229):
 *   PhoneAppdataUpdateSerializer accepts:
 *     - country (ChoiceField, alpha-2 codes only — Q3a hybrid validation)
 *     - country_code, about, phone_status (free-text / nullable)
 *     - is_business, verified, premium (booleans)
 *
 *   It does NOT accept `phone_number`, `region`, or `display_name` — those
 *   columns do not exist on `PhoneAppdata`. The plan slice mentions them
 *   as a placeholder; we drop them here to match reality.
 */

export interface PhoneAppdataFormState {
  country: string;
  country_code: string;
  about: string;
  phone_status: string;
  verified: boolean | null;
  is_business: boolean | null;
  premium: boolean | null;
}

export function initialPhoneAppdata(seed: Record<string, unknown>): PhoneAppdataFormState {
  return {
    country: typeof seed.country === "string" ? seed.country : "",
    country_code: typeof seed.country_code === "string" ? seed.country_code : "",
    about: typeof seed.about === "string" ? seed.about : "",
    phone_status: typeof seed.phone_status === "string" ? seed.phone_status : "",
    verified: typeof seed.verified === "boolean" ? seed.verified : null,
    is_business: typeof seed.is_business === "boolean" ? seed.is_business : null,
    premium: typeof seed.premium === "boolean" ? seed.premium : null,
  };
}

export function diffPhoneAppdata(
  form: PhoneAppdataFormState,
  seed: Record<string, unknown>,
): Record<string, unknown> {
  const initial = initialPhoneAppdata(seed);
  const out: Record<string, unknown> = {};
  for (const key of Object.keys(form) as Array<keyof PhoneAppdataFormState>) {
    if (form[key] !== initial[key]) {
      out[key] = form[key];
    }
  }
  return out;
}

interface Props {
  form: PhoneAppdataFormState;
  setForm: (next: PhoneAppdataFormState) => void;
}

export function PhoneAppdataForm({ form, setForm }: Props) {
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 12 }}>
      <label
        htmlFor="ir661-edit-entity-phone-country"
        style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
      >
        Country
        <select
          id="ir661-edit-entity-phone-country"
          className="iris-input"
          value={form.country}
          onChange={(e) => setForm({ ...form, country: e.target.value })}
          style={{ width: "100%", marginTop: 4 }}
        >
          <option value="">—</option>
          {COUNTRY_ALPHA2_CODES.map((code) => (
            <option key={code} value={code}>
              {code}
            </option>
          ))}
        </select>
      </label>

      <label
        htmlFor="ir661-edit-entity-phone-country-code"
        style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
      >
        Country Code
        <input
          id="ir661-edit-entity-phone-country-code"
          className="iris-input"
          value={form.country_code}
          onChange={(e) => setForm({ ...form, country_code: e.target.value })}
          style={{ width: "100%", marginTop: 4 }}
        />
      </label>

      <label
        htmlFor="ir661-edit-entity-phone-about"
        style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
      >
        About
        <input
          id="ir661-edit-entity-phone-about"
          className="iris-input"
          value={form.about}
          onChange={(e) => setForm({ ...form, about: e.target.value })}
          style={{ width: "100%", marginTop: 4 }}
        />
      </label>

      <label
        htmlFor="ir661-edit-entity-phone-status"
        style={{ display: "block", fontSize: 12, color: "var(--iris-fg-3)" }}
      >
        Phone Status
        <input
          id="ir661-edit-entity-phone-status"
          className="iris-input"
          value={form.phone_status}
          onChange={(e) => setForm({ ...form, phone_status: e.target.value })}
          style={{ width: "100%", marginTop: 4 }}
        />
      </label>

      <label
        htmlFor="ir661-edit-entity-phone-verified"
        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
      >
        <input
          id="ir661-edit-entity-phone-verified"
          type="checkbox"
          checked={form.verified === true}
          onChange={(e) => setForm({ ...form, verified: e.target.checked })}
        />
        Verified
      </label>

      <label
        htmlFor="ir661-edit-entity-phone-business"
        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
      >
        <input
          id="ir661-edit-entity-phone-business"
          type="checkbox"
          checked={form.is_business === true}
          onChange={(e) => setForm({ ...form, is_business: e.target.checked })}
        />
        Business
      </label>

      <label
        htmlFor="ir661-edit-entity-phone-premium"
        style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12 }}
      >
        <input
          id="ir661-edit-entity-phone-premium"
          type="checkbox"
          checked={form.premium === true}
          onChange={(e) => setForm({ ...form, premium: e.target.checked })}
        />
        Premium
      </label>
    </div>
  );
}
